import React, { useState } from "react";
import {
    Box, Wrap, WrapItem, Stack, Text, Input, IconButton, Icon, Tag, TagLabel, TagCloseButton, useColorModeValue,
} from "@chakra-ui/react";
import { MdAddCircleOutline } from "react-icons/md";

/**
 * Renders a horizontal list of item tags with delete buttons.
 * @param {object} props
 * @param {string[]} props.items - Items to display as tags
 * @param {function} props.onRemove - Callback with index to remove an item
 * @returns {React.ReactElement}
 */
function ItemTagList({ items, onRemove }) {
    return (
        <Wrap spacing="4px" mb="8px">
            {items.map((item, idx) => (
                <WrapItem key={idx}>
                    <Tag size="sm" borderRadius="full" variant="subtle">
                        <TagLabel>{item}</TagLabel>
                        <TagCloseButton onClick={() => onRemove(idx)} />
                    </Tag>
                </WrapItem>
            ))}
        </Wrap>
    );
}

/**
 * Renders an editable list of items (strings) with add/remove.
 * @param {object} props
 * @param {string[]} props.items - Current items array
 * @param {function} props.onAdd - Callback to add an item
 * @param {function} props.onRemove - Callback to remove an item by index
 * @returns {React.ReactElement}
 */
function ItemsEditor({ items, onAdd, onRemove }) {
    const [newItem, setNewItem] = useState("");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    /** Adds the current item text and clears the input field. */
    const handleAdd = () => {
        if (!newItem.trim()) return;
        onAdd(newItem.trim());
        setNewItem("");
    };

    return (
        <Box mt="8px">
            <Text fontSize="0.75rem" mb="4px" color={labelColor}>Items</Text>
            <ItemTagList items={items} onRemove={onRemove} />
            <Stack direction="row" spacing="8px" align="center">
                <Input size="sm" borderRadius="8px" placeholder="New item..." value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
                <IconButton aria-label="Ajouter un item" size="sm" variant="ghost"
                    icon={<Icon as={MdAddCircleOutline} />} onClick={handleAdd} />
            </Stack>
        </Box>
    );
}

export { ItemsEditor };
export default ItemsEditor;
